import type { ResellerLicence, ResellerOverview, XopQuote } from "@xop-any-time/shared";
import { applianceLicenceStatus, centsToUnits } from "@xop-any-time/shared";

import { dateOnly, money } from "../../../lib/format.js";
import type { ReleaseRow } from "../releases/api.js";

/**
 * What a reseller should know about when it comes back — as pure rules.
 *
 * The portal has no notification table and needs none: everything worth a
 * bell is ALREADY on a page somewhere (an overdue invoice, a quote waiting
 * for a decision, a licence about to lapse, a version XOP just shipped).
 * This module reads those four feeds — the same queries the pages run — and
 * turns them into one list, newest first, each row pointing at the thing
 * itself. "New since last time" is the only state the portal keeps, and it
 * keeps it in the browser (`seenStore` below), per signed-in person: the
 * keys a person has already opened the bell on. A row whose key is not in
 * that set is NEW, and the badge counts those.
 *
 * WHY KEYS AND NOT A TIMESTAMP. A licence that was fine on Monday and
 * expiring on Thursday has no "created at" to compare; an invoice that has
 * been overdue for a month is not news twice. A key per FACT ("licence 7 is
 * expiring", "invoice 31 is overdue") makes each fact new exactly once, and
 * lets it become new again if it changes state (the key changes).
 */
export type NotificationKind = "invoice" | "quote" | "licence" | "release";

export interface NotificationItem {
  /** Stable per FACT, so the same fact is new exactly once. */
  key: string;
  kind: NotificationKind;
  title: string;
  detail: string;
  /** Where the row goes — the thing itself, never a list. */
  to: string;
  /** ISO date the fact is dated by (due, valid-until, expires, released);
   *  ordering only. */
  at: string;
  severity: "warning" | "error" | "info";
}

export interface NotificationFeeds {
  overview?: ResellerOverview | null | undefined;
  quotes?: readonly XopQuote[] | null | undefined;
  licences?: readonly ResellerLicence[] | null | undefined;
  releases?: readonly ReleaseRow[] | null | undefined;
}

/** The feeds into rows. Each feed is optional: a page a person may not see
 * (no `invoices:view`) contributes nothing rather than an error. */
export function buildNotifications(feeds: NotificationFeeds): NotificationItem[] {
  const items: NotificationItem[] = [];

  for (const invoice of feeds.overview?.overdueInvoices ?? []) {
    items.push({
      key: `invoice:${invoice.id}:overdue`,
      kind: "invoice",
      title: `Invoice ${invoice.number ?? `#${invoice.id}`} is overdue`,
      detail: [
        invoice.total !== null && invoice.total !== undefined
          ? money(invoice.total, invoice.currency)
          : null,
        invoice.dueOn ? `due ${dateOnly(invoice.dueOn)}` : null,
      ]
        .filter(Boolean)
        .join(" · "),
      to: `/invoices/${invoice.id}`,
      at: invoice.dueOn ?? "",
      severity: "error",
    });
  }

  for (const quote of feeds.quotes ?? []) {
    if (quote.effectiveStatus !== "open") continue;
    items.push({
      key: `quote:${quote.id}:open`,
      kind: "quote",
      title: `Quote ${quote.number ?? `#${quote.id}`} is waiting for your decision`,
      detail: [
        money(centsToUnits(quote.totalCents), quote.currency),
        quote.validUntil ? `valid until ${dateOnly(quote.validUntil)}` : null,
      ]
        .filter(Boolean)
        .join(" · "),
      to: `/quotes/${quote.id}`,
      at: quote.issuedAt ?? quote.validUntil ?? "",
      severity: "info",
    });
  }

  for (const licence of feeds.licences ?? []) {
    const status = applianceLicenceStatus(licence);
    if (status !== "expiring" && status !== "expired") continue;
    const box = licence.label || licence.hardwareAddress;
    items.push({
      key: `licence:${licence.id}:${status}`,
      kind: "licence",
      title:
        status === "expired"
          ? `Licence for ${licence.customerName} has expired`
          : `Licence for ${licence.customerName} expires soon`,
      detail: [box, licence.expiresOn ? `ends ${dateOnly(licence.expiresOn)}` : null]
        .filter(Boolean)
        .join(" · "),
      to: `/licences/${licence.id}`,
      at: licence.expiresOn ?? "",
      severity: status === "expired" ? "error" : "warning",
    });
  }

  const byId = new Map((feeds.releases ?? []).map((r) => [r.id, r]));
  for (const version of feeds.releases ?? []) {
    // A shipped VERSION is the news; a release folder is just its name.
    if (!version.released || version.parent_release_id == null) continue;
    const line = byId.get(version.parent_release_id);
    const folder = line?.version ?? version.version.split(".").slice(0, 2).join(".");
    items.push({
      key: `release:${version.id}:released`,
      kind: "release",
      title: `Version ${version.version} is available`,
      detail: [version.product_name, version.release_date ? `released ${dateOnly(version.release_date)}` : null]
        .filter(Boolean)
        .join(" · "),
      to: `/releases/${encodeURIComponent(folder)}?version=${encodeURIComponent(version.version)}`,
      at: version.release_date ?? "",
      severity: "info",
    });
  }

  // Newest first; a row with no date sorts last. Ties keep feed order.
  return items
    .map((item, index) => ({ item, index }))
    .sort((a, b) => (b.item.at || "").localeCompare(a.item.at || "") || a.index - b.index)
    .map(({ item }) => item);
}

/** How many rows this person has not opened the bell on yet. */
export function unseenCount(items: readonly NotificationItem[], seen: ReadonlySet<string>): number {
  return items.filter((item) => !seen.has(item.key)).length;
}

/**
 * The per-person memory of what has been seen — in the browser, keyed by
 * email, so two people sharing a machine do not clear each other's bell.
 * Wrapped in try/catch throughout: storage can be absent (a private window,
 * a blocked site) and the bell must still render, merely never "read".
 */
export const SEEN_STORAGE_PREFIX = "xop-any-time:notifications:seen:";

export function loadSeen(email: string): Set<string> {
  try {
    const raw = window.localStorage.getItem(SEEN_STORAGE_PREFIX + email.toLowerCase());
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(parsed) ? parsed.filter((k): k is string => typeof k === "string") : []);
  } catch {
    return new Set();
  }
}

/** Remember every CURRENT key as seen. Keys of facts that no longer exist
 * are dropped, so the set cannot grow forever. */
export function saveSeen(email: string, items: readonly NotificationItem[]): Set<string> {
  const next = new Set(items.map((item) => item.key));
  try {
    window.localStorage.setItem(SEEN_STORAGE_PREFIX + email.toLowerCase(), JSON.stringify([...next]));
  } catch {
    // Storage is a convenience; the bell works without it.
  }
  return next;
}

export const KIND_LABEL: Record<NotificationKind, string> = {
  invoice: "Invoices",
  quote: "Quotes",
  licence: "Licences",
  release: "Software Releases",
};
